import { useEffect, useMemo, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import ReactFlow, { Background, Controls, MiniMap, Handle, Position } from "reactflow";
import "reactflow/dist/style.css";
import { api, apiError } from "@/lib/api";
import { logger } from "@/lib/logger";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  Activity, CheckCircle2, Loader2, Clock, PauseCircle, ShieldQuestion, XCircle,
  RefreshCw, Ban, CircleDashed, Wifi, WifiOff, ChevronDown, ChevronUp, Play,
  AlertTriangle,
} from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

// Status vocabulary — colour AND text+icon (never colour-only).
const STATUS = {
  completed: { label: "Completed", cls: "text-emerald-300 bg-emerald-500/10 border-emerald-500/30", dot: "bg-emerald-400", Icon: CheckCircle2 },
  running: { label: "Running", cls: "text-sky-300 bg-sky-500/10 border-sky-500/30", dot: "bg-sky-400", Icon: Loader2 },
  pending: { label: "Pending", cls: "text-slate-300 bg-slate-500/10 border-slate-500/30", dot: "bg-slate-400", Icon: CircleDashed },
  queued: { label: "Queued", cls: "text-slate-300 bg-slate-500/10 border-slate-500/30", dot: "bg-slate-400", Icon: Clock },
  waiting: { label: "Waiting", cls: "text-amber-300 bg-amber-500/10 border-amber-500/30", dot: "bg-amber-400", Icon: Clock },
  approval: { label: "Approval Required", cls: "text-violet-300 bg-violet-500/10 border-violet-500/30", dot: "bg-violet-400", Icon: ShieldQuestion },
  failed: { label: "Failed", cls: "text-rose-300 bg-rose-500/10 border-rose-500/30", dot: "bg-rose-400", Icon: XCircle },
  retrying: { label: "Retrying", cls: "text-orange-300 bg-orange-500/10 border-orange-500/30", dot: "bg-orange-400", Icon: RefreshCw },
  cancelled: { label: "Cancelled", cls: "text-slate-400 bg-slate-600/10 border-slate-600/30", dot: "bg-slate-500", Icon: Ban },
  skipped: { label: "Skipped", cls: "text-slate-400 bg-slate-600/10 border-slate-600/30", dot: "bg-slate-500", Icon: Ban },
  stale: { label: "Stale / Unknown", cls: "text-yellow-300 bg-yellow-500/10 border-yellow-500/30", dot: "bg-yellow-400", Icon: ShieldQuestion },
};
const meta = (s) => STATUS[s] || STATUS.pending;

function Pill({ status, testid }) {
  const m = meta(status);
  const Icon = m.Icon;
  return (
    <span className={`inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full border ${m.cls}`} data-testid={testid}>
      <Icon className={`w-3 h-3 ${status === "running" ? "animate-spin" : ""}`} /> {m.label}
    </span>
  );
}

// Phase 30D — Stale badge (display-only, driven by API `stale` flag).
function StaleBadge({ className = "" }) {
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-amber-500/15 text-amber-300 border border-amber-500/30 ${className}`}
      data-testid="stale-badge" title="Worker 10+ dəqiqədir səssizdir">
      <AlertTriangle className="w-3 h-3" /> Stale
    </span>
  );
}

// ---- React Flow custom node (free OSS) ----
function StageNode({ data }) {
  const m = meta(data.status);
  const Icon = m.Icon;
  const hasCost = data.cost !== null && data.cost !== undefined && data.cost > 0;
  return (
    <div className={`px-3 py-2 rounded-lg border min-w-[150px] ${data.selected ? "ring-2 ring-violet-500" : ""} ${m.cls}`} data-testid={`map-node-${data.id}`}>
      <Handle type="target" position={Position.Left} className="!bg-slate-600" />
      <div className="flex items-center gap-1.5">
        <Icon className={`w-3.5 h-3.5 ${data.status === "running" ? "animate-spin" : ""}`} />
        <span className="text-xs font-medium text-slate-100 truncate">{data.name}</span>
        {data.stale && <StaleBadge />}
      </div>
      <div className="text-[10px] mt-0.5 opacity-80">{m.label}{data.attempt > 1 ? ` · attempt ${data.attempt}` : ""}</div>
      <div className="text-[10px] opacity-60 truncate">{data.executor}</div>
      {hasCost && <div className="text-[10px] mt-0.5 text-emerald-300/80 font-mono" data-testid={`map-node-cost-${data.id}`}>${Number(data.cost).toFixed(4)}</div>}
      <Handle type="source" position={Position.Right} className="!bg-slate-600" />
    </div>
  );
}
const nodeTypes = { stage: StageNode };

const fmtMs = (ms) => (ms === null || ms === undefined ? "Not recorded" : ms < 1000 ? `${ms}ms` : `${(ms / 1000).toFixed(1)}s`);
const fmtCost = (c) => (c === null || c === undefined ? "Not recorded" : `$${Number(c).toFixed(4)}`);
const fmtTime = (t) => (t ? new Date(t).toLocaleString() : "Not recorded");

function DetailPanel({ run, node, onControl, busy }) {
  if (!run) return null;
  const n = node;
  return (
    <div className="bg-[#0d1424] border border-slate-800 rounded-xl p-4 space-y-3 overflow-y-auto" data-testid="monitor-detail-panel">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <h3 className="font-display font-semibold text-slate-100 break-words">{run.video_title || run.run_id}</h3>
          <p className="text-xs text-slate-500">{run.kind_label} · {run.channel_name && run.channel_name !== null ? run.channel_name : "Not recorded"}</p>
        </div>
        <Pill status={run.status} testid="detail-run-status" />
        {run.stale && <StaleBadge />}
      </div>
      {run.stale && (
        <div className="flex items-center gap-2 text-xs text-amber-300/90 bg-amber-500/10 border border-amber-500/25 rounded-lg px-3 py-2" data-testid="detail-stale-warning">
          <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0" />
          <span>Worker 10+ dəqiqədir səssizdir — son heartbeat: {fmtTime(run.last_heartbeat)}</span>
        </div>
      )}
      <div className="grid grid-cols-2 gap-2 text-xs">
        <Info k="Run ID" v={run.run_id} />
        <Info k="Stage progress" v={run.stage_progress} />
        <Info k="Created" v={fmtTime(run.created_at)} />
        <Info k="Updated" v={fmtTime(run.updated_at)} />
        <Info k="Cost (actual)" v={fmtCost(run.cost_actual)} />
        <Info k="Cost (estimated)" v={fmtCost(run.cost_estimated)} />
        {run.kind === "autopilot" && run.cost_detail === "legacy" && (
          <div className="col-span-2 text-[11px] text-amber-400/80 italic" data-testid="detail-cost-legacy">Legacy – yalnız ümumi xərc</div>
        )}
        {run.kind === "publish" && <Info k="Attempts" v={String(run.attempts ?? 0)} />}
        {run.kind === "publish" && <Info k="Current stage" v={run.current_stage || "—"} />}
      </div>
      {run.kind === "autopilot" && run.cost_detail === "per_step" && run.step_costs && run.step_costs.length > 0 && (
        <div className="border-t border-slate-800 pt-3 space-y-2" data-testid="detail-step-costs">
          <p className="text-xs text-slate-500 font-medium">Per-step cost breakdown</p>
          <div className="space-y-1">
            {(() => {
              const map = {};
              run.step_costs.forEach(sc => { map[sc.step] = (map[sc.step] || 0) + (sc.cost_usd || 0); });
              return Object.entries(map).map(([step, cost]) => (
                <div key={step} className="flex items-center justify-between text-[11px] text-slate-400">
                  <span className="font-mono">{step}</span>
                  <span className="text-emerald-300/80 font-mono">${Number(cost).toFixed(4)}</span>
                </div>
              ));
            })()}
          </div>
        </div>
      )}
      {run.kind === "publish" && run.last_error && (
        <div className="text-xs text-rose-300 bg-rose-500/10 border border-rose-500/30 rounded p-2 break-words" data-testid="detail-publish-error">{run.last_error}</div>
      )}
      <div className="flex flex-wrap gap-2">
        {run.links?.project && run.links.project !== null && <a className="text-xs text-violet-300 underline" href={run.links.project} data-testid="detail-project-link">Open project</a>}
        {run.links?.channel && run.links.channel !== null && <a className="text-xs text-violet-300 underline" href={run.links.channel}>Open channel</a>}
        {run.artifact?.stream && (
          <a className="text-xs text-emerald-300 underline" href={`${BACKEND}${run.artifact.stream}`} target="_blank" rel="noreferrer" data-testid="detail-artifact-link">
            ▶ Play render v{run.artifact.version}
          </a>
        )}
      </div>

      {n && (
        <div className="border-t border-slate-800 pt-3 space-y-2" data-testid="detail-step">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-slate-200">{n.name}</span>
            <Pill status={n.status} testid="detail-step-status" />
          </div>
          <div className="grid grid-cols-2 gap-2 text-xs">
            <Info k="Executor" v={n.executor} />
            <Info k="Attempt" v={String(n.attempt ?? 1)} />
            <Info k="Started" v={fmtTime(n.started_at)} />
            <Info k="Finished" v={fmtTime(n.finished_at)} />
            <Info k="Run duration" v={fmtMs(n.duration_ms)} />
            <Info k="Queue wait" v={fmtMs(n.queue_wait_ms)} />
            <Info k="Cost" v={fmtCost(n.cost)} />
          </div>
          {n.detail && n.detail !== null && <p className="text-xs text-slate-400 break-words">{n.detail}</p>}
          {n.error && n.error !== null && (
            <div className="text-xs text-rose-300 bg-rose-500/10 border border-rose-500/30 rounded p-2 break-words" data-testid="detail-step-error">{n.error}</div>
          )}
          {n.artifact?.link && <a className="text-xs text-emerald-300 underline" href={n.artifact.link}>Open artifact</a>}
        </div>
      )}

      {run.kind === "publish" && run.stage_history && run.stage_history.length > 0 && (
        <div className="border-t border-slate-800 pt-3 space-y-2" data-testid="detail-stage-history">
          <p className="text-xs text-slate-500 font-medium">Publish Stage History</p>
          <div className="space-y-1.5">
            {run.stage_history.map((h, i) => (
              <div key={i} className="flex items-center gap-2 text-[11px] text-slate-400">
                <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${
                  h.stage === "published" ? "bg-emerald-400" : h.stage === "upload" ? "bg-sky-400" : "bg-violet-400"
                }`} />
                <span className="text-slate-500 flex-shrink-0">{fmtTime(h.at)}</span>
                <span className="font-mono flex-shrink-0">{h.stage}</span>
                <span className="truncate">{h.detail}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="border-t border-slate-800 pt-3 space-y-2">
        <p className="text-xs text-slate-500">Controls (server-authorized + audited)</p>
        <div className="flex flex-wrap gap-2">
          <Button size="sm" disabled={!run.controls?.retry || busy} onClick={() => onControl("retry")}
            className="bg-orange-600 hover:bg-orange-500 text-white disabled:opacity-40" data-testid="monitor-retry-btn">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><RefreshCw className="w-3.5 h-3.5 mr-1" /> Safe Retry</>}
          </Button>
          <Button size="sm" disabled={!run.controls?.cancel || busy} onClick={() => onControl("cancel")}
            className="bg-slate-700 hover:bg-slate-600 text-slate-100 disabled:opacity-40" data-testid="monitor-cancel-btn">
            <Ban className="w-3.5 h-3.5 mr-1" /> Cancel
          </Button>
        </div>
        <p className="text-[11px] text-slate-500">{run.controls?.retry_reason}</p>
      </div>
    </div>
  );
}
const Info = ({ k, v }) => (
  <div><span className="text-slate-500">{k}</span><div className="text-slate-300 break-words">{v ?? "Not recorded"}</div></div>
);

export default function ProductionMonitor() {
  const [params, setParams] = useSearchParams();
  const [overview, setOverview] = useState(null);
  const [runs, setRuns] = useState(null);
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState({ channel_id: "", kind: "", status: "", q: "" });
  const [selectedId, setSelectedId] = useState(params.get("run") || null);
  const [detail, setDetail] = useState(null);
  const [selNode, setSelNode] = useState(null);
  const [busy, setBusy] = useState(false);
  const [conn, setConn] = useState({ live: false, at: null });
  const [timelineOpen, setTimelineOpen] = useState(false);
  const [disabled, setDisabled] = useState(false);
  const [staleBannerDismissed, setStaleBannerDismissed] = useState(false);

  const qs = useMemo(() => {
    const p = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => { if (v) p.set(k, v); });
    return p.toString();
  }, [filters]);

  const loadList = useCallback(async () => {
    try {
      const [ov, rl] = await Promise.all([
        api.get(`/monitor/overview?${qs}`),
        api.get(`/monitor/runs?${qs}&page=${page}&page_size=20`),
      ]);
      setOverview(ov.data); setRuns(rl.data);
      setConn({ live: true, at: new Date() });
      setDisabled(false);
    } catch (e) {
      if (e?.response?.status === 404) { setDisabled(true); setConn({ live: false, at: new Date() }); return; }
      setConn({ live: false, at: new Date() });
      logger.error("monitor list failed", e);
    }
  }, [qs, page]);

  const loadDetail = useCallback(async (id) => {
    if (!id) { setDetail(null); return; }
    try {
      const { data } = await api.get(`/monitor/runs/${encodeURIComponent(id)}`);
      setDetail(data);
      setConn({ live: true, at: new Date() });
      setSelNode((prev) => prev ? (data.workflow.nodes.find((n) => n.id === prev.id) || null) : prev);
    } catch (e) {
      setConn({ live: false, at: new Date() });
      logger.error("monitor detail failed", e);
    }
  }, []);

  // initial + filter/page changes
  useEffect(() => { loadList(); }, [loadList]);
  // selection → detail + deep link
  useEffect(() => {
    if (selectedId) { setParams((p) => { p.set("run", selectedId); return p; }, { replace: true }); loadDetail(selectedId); }
    else { setParams((p) => { p.delete("run"); return p; }, { replace: true }); setDetail(null); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId]);

  // polling — 4s when an active run is in view, back off to 12s when idle.
  useEffect(() => {
    const activeInView = (detail && ["running", "retrying", "waiting", "approval", "queued"].includes(detail.status))
      || (overview && overview.counts && overview.counts.active > 0);
    const interval = activeInView ? 4000 : 12000;
    const iv = setInterval(() => { loadList(); if (selectedId) loadDetail(selectedId); }, interval);
    return () => clearInterval(iv);
  }, [loadList, loadDetail, selectedId, detail, overview]);

  const rfNodes = useMemo(() => {
    if (!detail) return [];
    const isRunStale = detail.stale === true;
    return detail.workflow.nodes.map((n, i) => ({
      id: n.id, type: "stage", position: { x: i * 210, y: (i % 2) * 20 },
      data: { ...n, selected: selNode?.id === n.id, stale: isRunStale && (n.status === "running" || n.status === "queued") },
    }));
  }, [detail, selNode]);
  const rfEdges = useMemo(() => {
    if (!detail) return [];
    return detail.workflow.edges.map((e) => ({
      id: `${e.source}-${e.target}`, source: e.source, target: e.target, animated: false,
      style: { stroke: e.taken ? "#64748b" : "#334155", strokeDasharray: e.taken ? "0" : "5 5", opacity: e.taken ? 1 : 0.4 },
    }));
  }, [detail]);

  const onControl = async (action) => {
    setBusy(true);
    try {
      await api.post(`/monitor/runs/${encodeURIComponent(selectedId)}/controls/${action}`);
      toast.success(`${action === "retry" ? "Safe retry" : "Cancel"} requested`);
      await loadDetail(selectedId); await loadList();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  if (disabled) {
    return (
      <div className="p-8" data-testid="monitor-disabled">
        <h1 className="text-2xl font-display font-bold text-slate-100 flex items-center gap-2"><Activity className="w-6 h-6" /> Production Monitor</h1>
        <p className="text-slate-400 mt-3">This section is turned off. Enable <b>Production Monitor</b> in Settings to use it.</p>
      </div>
    );
  }

  const counts = overview?.counts || { active: 0, queued: 0, completed: 0, failed: 0, stale: 0, total: 0 };
  const cards = [
    { k: "active", label: "Active", status: "running" },
    { k: "queued", label: "Queued", status: "queued" },
    { k: "completed", label: "Completed", status: "completed" },
    { k: "failed", label: "Failed", status: "failed" },
    { k: "stale", label: "Stale", status: "stale" },
  ];

  return (
    <div className="p-6 space-y-5" data-testid="production-monitor">
      {/* Top bar */}
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="text-2xl font-display font-bold text-slate-100 flex items-center gap-2"><Activity className="w-6 h-6 text-violet-400" /> Production Monitor</h1>
        <span className="text-[11px] px-2 py-0.5 rounded-full bg-slate-800 text-slate-300 border border-slate-700" data-testid="monitor-env">Production</span>
        <span className={`inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full border ${conn.live ? "text-emerald-300 bg-emerald-500/10 border-emerald-500/30" : "text-rose-300 bg-rose-500/10 border-rose-500/30"}`} data-testid="monitor-connection">
          {conn.live ? <Wifi className="w-3 h-3" /> : <WifiOff className="w-3 h-3" />} {conn.live ? "Live" : "Disconnected"}
        </span>
        <span className="text-[11px] text-slate-500" data-testid="monitor-last-updated">Last updated: {conn.at ? conn.at.toLocaleTimeString() : "—"}</span>
        <Button size="sm" variant="ghost" className="ml-auto text-slate-400" onClick={() => { loadList(); if (selectedId) loadDetail(selectedId); }} data-testid="monitor-refresh"><RefreshCw className="w-4 h-4" /></Button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2">
        <Input placeholder="Search title / channel / id…" value={filters.q} onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))}
          className="max-w-xs bg-[#0d1424] border-slate-700 text-slate-200 text-sm" data-testid="monitor-search" />
        <Select value={filters.kind || "all"} onValueChange={(v) => setFilters((f) => ({ ...f, kind: v === "all" ? "" : v }))}>
          <SelectTrigger className="w-40 bg-[#0d1424] border-slate-700 text-slate-200 text-sm" data-testid="monitor-filter-kind"><SelectValue placeholder="All kinds" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All kinds</SelectItem>
            <SelectItem value="autopilot">Autopilot</SelectItem>
            <SelectItem value="render">Render</SelectItem>
            <SelectItem value="goal">Goal</SelectItem>
            <SelectItem value="publish">Publish</SelectItem>
          </SelectContent>
        </Select>
        <Select value={filters.status || "all"} onValueChange={(v) => setFilters((f) => ({ ...f, status: v === "all" ? "" : v }))}>
          <SelectTrigger className="w-40 bg-[#0d1424] border-slate-700 text-slate-200 text-sm" data-testid="monitor-filter-status"><SelectValue placeholder="All status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All status</SelectItem>
            {["running", "queued", "waiting", "approval", "completed", "failed", "cancelled"].map((s) => <SelectItem key={s} value={s}>{meta(s).label}</SelectItem>)}
          </SelectContent>
        </Select>
        <span className="text-[11px] text-slate-500" data-testid="monitor-scope">Scope: {overview?.scope_label || "—"}</span>
      </div>

      {/* Overview cards */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        {cards.map((c) => {
          const m = meta(c.status);
          return (
            <div key={c.k} className={`rounded-xl border p-4 ${m.cls}`} data-testid={`overview-card-${c.k}`}>
              <div className="flex items-center gap-2 text-xs opacity-90"><m.Icon className="w-4 h-4" /> {c.label}</div>
              <div className="text-3xl font-bold mt-1 text-slate-100" data-testid={`overview-count-${c.k}`}>{counts[c.k]}</div>
            </div>
          );
        })}
      </div>

      {/* Phase 34: Stale Alerts banner */}
      {!staleBannerDismissed && counts.stale > 0 && runs?.items && (() => {
        const staleRuns = runs.items.filter((r) => r.stale);
        if (!staleRuns.length) return null;
        return (
          <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl px-4 py-3 flex items-start gap-3" data-testid="stale-alert-banner">
            <AlertTriangle className="w-5 h-5 text-amber-400 shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-amber-200" data-testid="stale-banner-title">
                {counts.stale} köhnəlmiş iş tapıldı · {counts.stale} stale run(s) detected
              </p>
              <div className="mt-1 space-y-0.5">
                {staleRuns.slice(0, 5).map((r) => (
                  <button key={r.run_id} onClick={() => setSelectedId(r.run_id)}
                    className="block text-xs text-amber-300/80 hover:text-amber-200 truncate max-w-full transition-colors"
                    data-testid={`stale-banner-link-${r.run_id}`}>
                    → {r.video_title || r.run_id} · {r.kind_label} · {r.channel_name || "—"}
                  </button>
                ))}
                {staleRuns.length > 5 && <span className="text-xs text-amber-400/60">…və {staleRuns.length - 5} daha</span>}
              </div>
            </div>
            <button onClick={() => setStaleBannerDismissed(true)} className="text-amber-400/60 hover:text-amber-300 transition-colors p-1"
              data-testid="stale-banner-dismiss" aria-label="Dismiss stale alert banner">
              <Ban className="w-4 h-4" />
            </button>
          </div>
        );
      })()}

      <div className="grid lg:grid-cols-[1fr_360px] gap-4">
        {/* Left: Workflow map (when a run selected) + Run history */}
        <div className="space-y-4 min-w-0">
          {detail ? (
            <div className="bg-[#0d1424] border border-slate-800 rounded-xl overflow-hidden" data-testid="monitor-workflow-map">
              <div className="flex items-center justify-between px-4 py-2 border-b border-slate-800">
                <span className="text-sm text-slate-300">Workflow Map · {detail.kind_label}</span>
                <Button size="sm" variant="ghost" className="text-slate-400" onClick={() => setSelectedId(null)} data-testid="monitor-close-run">Close</Button>
              </div>
              {/* Desktop: interactive canvas */}
              <div className="hidden md:block" style={{ height: 320 }}>
                <ReactFlow nodes={rfNodes} edges={rfEdges} nodeTypes={nodeTypes} fitView
                  onNodeClick={(_e, n) => setSelNode(detail.workflow.nodes.find((x) => x.id === n.id) || null)}
                  proOptions={{ hideAttribution: true }} nodesDraggable={false} nodesConnectable={false}>
                  <Background color="#1e293b" gap={16} />
                  <Controls showInteractive={false} />
                  <MiniMap pannable zoomable className="!bg-slate-900" />
                </ReactFlow>
              </div>
              {/* Mobile: stage list alternative */}
              <div className="md:hidden divide-y divide-slate-800" data-testid="monitor-stage-list">
                {detail.workflow.nodes.map((n) => (
                  <button key={n.id} onClick={() => setSelNode(n)} className="w-full flex items-center justify-between px-4 py-2.5 text-left">
                    <span className="text-sm text-slate-200">{n.name}</span><Pill status={n.status} />
                  </button>
                ))}
              </div>
              {/* Event timeline (collapsible, paginated to last 50) */}
              <div className="border-t border-slate-800">
                <button className="w-full flex items-center justify-between px-4 py-2 text-xs text-slate-400" onClick={() => setTimelineOpen((o) => !o)} data-testid="monitor-timeline-toggle">
                  Event timeline ({detail.events?.length || 0}) {timelineOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                </button>
                {timelineOpen && (
                  <div className="max-h-48 overflow-y-auto px-4 pb-3 space-y-1" data-testid="monitor-timeline">
                    {(detail.events || []).slice(-50).map((ev) => (
                      <div key={ev.seq} className="flex items-center gap-2 text-[11px] text-slate-400">
                        <span className={`w-1.5 h-1.5 rounded-full ${meta(ev.type === "completed" ? "completed" : ev.type === "failed" ? "failed" : ev.type === "approval" ? "approval" : "running").dot}`} />
                        <span className="text-slate-500">{fmtTime(ev.at)}</span>
                        <span className="font-mono">{ev.type}</span>
                        <span className="truncate">{ev.detail}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="bg-[#0d1424] border border-slate-800 rounded-xl p-8 text-center text-slate-500" data-testid="monitor-map-empty">
              <Play className="w-6 h-6 mx-auto mb-2 opacity-50" /> Select a run below to view its live workflow map.
            </div>
          )}

          {/* Run history */}
          <div className="bg-[#0d1424] border border-slate-800 rounded-xl overflow-hidden" data-testid="monitor-run-history">
            <div className="px-4 py-2 border-b border-slate-800 text-sm text-slate-300">Run History {runs ? `(${runs.total})` : ""}</div>
            {!runs ? <div className="p-6 text-slate-500 text-sm">Loading…</div>
              : runs.items.length === 0 ? <div className="p-6 text-slate-500 text-sm" data-testid="monitor-empty">No runs recorded for this scope yet.</div>
              : (
                <div className="divide-y divide-slate-800">
                  {runs.items.map((r) => (
                    <button key={r.run_id} onClick={() => { setSelNode(null); setSelectedId(r.run_id); }}
                      className={`w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-slate-800/40 ${selectedId === r.run_id ? "bg-slate-800/60" : ""}`}
                      data-testid={`run-row-${r.run_id}`}>
                      <div className="min-w-0 flex-1">
                        <div className="text-sm text-slate-200 truncate">{r.video_title || r.run_id}</div>
                        <div className="text-[11px] text-slate-500 truncate">{r.kind_label} · {r.channel_name && r.channel_name !== null ? r.channel_name : "—"} · {r.stage_progress}</div>
                      </div>
                      <Pill status={r.status} testid={`run-status-${r.run_id}`} />
                      {r.stale && <StaleBadge className="ml-1" />}
                    </button>
                  ))}
                </div>
              )}
            {runs && runs.total > runs.page_size && (
              <div className="flex items-center justify-between px-4 py-2 border-t border-slate-800 text-xs text-slate-400">
                <Button size="sm" variant="ghost" disabled={page <= 1} onClick={() => setPage((p) => p - 1)} data-testid="monitor-prev">Prev</Button>
                <span>Page {runs.page} / {Math.ceil(runs.total / runs.page_size)}</span>
                <Button size="sm" variant="ghost" disabled={page >= Math.ceil(runs.total / runs.page_size)} onClick={() => setPage((p) => p + 1)} data-testid="monitor-next">Next</Button>
              </div>
            )}
          </div>
        </div>

        {/* Right: detail panel */}
        <div className="min-w-0" style={{ maxHeight: "calc(100vh - 120px)" }}>
          {detail ? <DetailPanel run={detail} node={selNode} onControl={onControl} busy={busy} />
            : <div className="bg-[#0d1424] border border-slate-800 rounded-xl p-6 text-slate-500 text-sm">Select a run to see run/step details, timings, cost and artifacts.</div>}
        </div>
      </div>
    </div>
  );
}
